import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback } from "react";
import { InvoiceLineItem } from "@common/types/invoice";
import { useUpdateInvoiceLineItem } from "@api/invoice.api";
import {
    AccrualAccountCell,
    UpdateLineItemAccountParams,
} from "@components/lineItems/AccrualAccountCell";
import { LineItemFallbackAccountType } from "@common/helpers/accounting";
import { isLineOwnedBySchedule } from "@components/invoices/views/scheduleCellState";
import { useIsInvoiceLocked } from "@components/invoices/useIsInvoiceLocked";
import { INVOICE_LOCKED_FIELD_TOOLTIP } from "@components/invoices/invoiceLockedFieldTooltip";
import {
    getLedgerIdFromColumnId,
    getLedgerNameFromColumnId,
} from "@lib/ledgerViewColumns";

export const AccrualLedgerCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, InvoiceLineItem, unknown>) => {
        const updateInvoiceLineItem = useUpdateInvoiceLineItem();
        const lineItem = row.original;
        const ledgerId = getLedgerIdFromColumnId(column.id);
        const ledgerName = getLedgerNameFromColumnId(column.id);
        const isLocked = useIsInvoiceLocked(lineItem.invoice);

        const onUpdateLineItemAccount = useCallback(
            ({
                workspaceId,
                lineItemId,
                accountOverrides,
                accountId,
            }: UpdateLineItemAccountParams) =>
                updateInvoiceLineItem.mutateAsync({
                    workspaceId,
                    lineItemId,
                    accountOverrides,
                    accountId,
                }),
            [updateInvoiceLineItem],
        );

        return (
            <AccrualAccountCell
                lineItemId={lineItem.id}
                lineItemAccountId={lineItem.accountId}
                accountOverrides={lineItem.accountOverrides}
                ledgerId={ledgerId}
                ledgerName={ledgerName}
                fallbackMode={LineItemFallbackAccountType.invoiceRevenue}
                entityId={lineItem.invoice?.entityId}
                isLockedBySchedule={isLineOwnedBySchedule(lineItem)}
                isLocked={isLocked}
                lockedTooltip={INVOICE_LOCKED_FIELD_TOOLTIP}
                onUpdateLineItemAccount={onUpdateLineItemAccount}
            />
        );
    },
);
